import { divideArrayIntoChunks, hexToBlob } from '@pkg/basic'
import type { InternalTransaction, TransactionIndex } from '@pkg/evm'
import { eq, sql } from 'drizzle-orm'
import {
  accountEvmTxIndexRelationColumnCount,
  evmInternalTransactionColumnCount,
  evmTxIndexColumnCount,
} from '../constants'
import {
  accountEvmTxIndexRelationTable,
  accountTable,
  evmInternalTransactionTable,
  evmTxIndexTable,
} from '../schema'
import type { DatabaseWithTransaction } from '../types'
import { getMaxInsertRowCount } from '../utils'

type SaveEvmTxIndexesParameters = {
  database: DatabaseWithTransaction
  accountId: number
  chainId: number
  internalTransactions: readonly InternalTransaction[]
  txIndexes: readonly TransactionIndex[]
  startTime: number
  transactionCount: number
  /** 通常取引・トークン・内部取引の取得を完了した最終ブロック。 */
  scannedToBlock?: number
  /** 部分取得では最終同期日時を消し、次回も続きの取得対象にする。 */
  isComplete?: boolean
}

export const saveEvmTxIndexes = async ({
  database: { transaction },
  accountId,
  chainId,
  internalTransactions,
  txIndexes,
  startTime,
  transactionCount,
  scannedToBlock,
  isComplete = true,
}: SaveEvmTxIndexesParameters): Promise<void> => {
  let toBlock = scannedToBlock ?? 0
  const blobTxIndexes: (Omit<TransactionIndex, 'hash' | 'input' | 'value'> & {
    hash: Uint8Array
    input?: Uint8Array
    value?: Uint8Array
  })[] = []
  for (const index of txIndexes) {
    if (index.blockNumber > toBlock) {
      toBlock = index.blockNumber
    }
    if (index.timestamp < startTime) {
      continue // Skip indexes before the portfolio start time
    }
    blobTxIndexes.push({
      blockNumber: index.blockNumber,
      timestamp: index.timestamp,
      hash: hexToBlob(index.hash),
      input: index.input === undefined ? undefined : hexToBlob(index.input),
      value:
        index.value === undefined
          ? undefined
          : hexToBlob(`0x${index.value.toString(16)}`),
    })
  }

  await transaction([
    (tx) => [
      // evmInternalTransaction に重複がない場合のみ保存
      ...divideArrayIntoChunks(
        internalTransactions,
        getMaxInsertRowCount(evmInternalTransactionColumnCount),
      ).map((internals) =>
        tx
          .insert(evmInternalTransactionTable)
          .values(
            internals.map((internal) => ({
              chainId,
              contractAddress:
                internal.contractAddress === undefined
                  ? Uint8Array.from([])
                  : hexToBlob(internal.contractAddress),
              from: hexToBlob(internal.from),
              gas: hexToBlob(`0x${internal.gas.toString(16)}`),
              hash: hexToBlob(internal.txHash),
              isError: internal.isError,
              to:
                internal.to === undefined
                  ? Uint8Array.from([])
                  : hexToBlob(internal.to),
              value: hexToBlob(`0x${internal.value.toString(16)}`),
            })),
          )
          .onConflictDoNothing(),
      ),
      // evmTxIndex に重複がない場合のみ保存
      ...divideArrayIntoChunks(
        blobTxIndexes,
        getMaxInsertRowCount(evmTxIndexColumnCount),
      ).map((indexes) =>
        tx
          .insert(evmTxIndexTable)
          .values(
            indexes.map((txIndex) => ({
              chainId,
              hash: txIndex.hash,
              blockNumber: txIndex.blockNumber,
              timestamp: new Date(txIndex.timestamp),
              input: txIndex.input,
              value: txIndex.value,
            })),
          )
          .onConflictDoNothing(),
      ),
      // accountEvmTxIndexRelation に重複がない場合のみ analyzed = false で保存
      ...divideArrayIntoChunks(
        blobTxIndexes,
        getMaxInsertRowCount(accountEvmTxIndexRelationColumnCount),
      ).map((indexes) =>
        tx
          .insert(accountEvmTxIndexRelationTable)
          .values(
            indexes.map((txIndex) => ({
              accountId,
              hash: txIndex.hash,
              analyzed: false,
            })),
          )
          .onConflictDoNothing(),
      ),
    ],
    (tx) => [
      // account を最新データに合わせて更新
      tx
        .update(accountTable)
        .set({
          syncing: sql`(EXISTS (SELECT 1 FROM ${accountEvmTxIndexRelationTable} WHERE ${accountEvmTxIndexRelationTable.accountId} = ${accountId} AND ${accountEvmTxIndexRelationTable.analyzed} = ${false}))`,
          lastSyncedAt: isComplete ? new Date() : null,
          evmToBlock: sql`(CASE WHEN ${accountTable.evmToBlock} IS NULL OR ${accountTable.evmToBlock} < ${toBlock} THEN ${toBlock} ELSE ${accountTable.evmToBlock} END)`,
          evmLastTxCount: isComplete ? transactionCount : undefined,
        })
        .where(eq(accountTable.id, accountId)),
    ],
  ])
}
